// BookTickets/components/Nav/index.tsx
'use client'

import { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'

import styles from './index.module.scss'
import StageIcon from './stage-icon.svg'

import { useBookingState } from '@booking/state/booking.context'
import { stageNumberOf } from '@booking/lib/bookingConfig'
import type { StageId } from '@booking/types/bookingConfig'
import type { Stage } from '@booking/types/booking'

type NavItem = {
  stageId: StageId
  stageNumber: number
  title: string
}

type NavProps = {
  stage: Stage
  onChange: (stage: Stage) => void
}

export default function BookingNav({ stage, onChange }: NavProps) {
  const t = useTranslations('booking')
  const { config } = useBookingState()
  const hasExtras = Boolean(config?.extras)

  // Maps each StageId to its i18n key in the nav namespace
  const NAV_LABEL_KEYS: Partial<Record<StageId, string>> = useMemo(
    () => ({
      extras: 'nav.extras',
      quantity: hasExtras ? 'nav.quantityAlt' : 'nav.quantity',
      datetime: hasExtras ? 'nav.sessionAlt' : 'nav.session',
      addons: 'nav.addons',
      customer: hasExtras ? 'nav.detailsAlt' : 'nav.details',
      payment: 'nav.checkout',
    }),
    [hasExtras],
  )

  const items = useMemo<NavItem[]>(() => {
    return config.navStages.map((stageId) => {
      const labelKey =
        stageId === 'extras' && config.extras
          ? (config.extras.navLabelKey ??
            config.extras.titleKey ??
            NAV_LABEL_KEYS.extras ??
            'nav.quantity')
          : (NAV_LABEL_KEYS[stageId] ?? 'nav.quantity')
      return {
        stageId,
        stageNumber: stageNumberOf(config, stageId),
        title: t(labelKey),
      }
    })
  }, [config, t, NAV_LABEL_KEYS])

  // True when the current stage is beyond the nav (e.g. thankyou, associate)
  const isPostFlow = useMemo(
    () => !config.navStages.includes(config.stages[stage - 1] as StageId),
    [config, stage],
  )

  const steps = useMemo(
    () => Array.from({ length: Math.max(items.length - 1, 0) }, (_, i) => i),
    [items.length],
  )

  return (
    <ul className={cn(styles.nav, 'gap-1')}>
      {steps.map((idx) => {
        const left = items[idx]
        const right = items[idx + 1]
        const isInitial = idx === 0
        const completed = left.stageNumber < stage

        return (
          <li key={idx} className={cn(completed && styles.completed, isInitial && styles.initial)}>
            {isInitial ? (
              <Flag
                className={cn(styles.first)}
                stage={left.stageNumber as Stage}
                value={String(idx + 1)}
                text={left.title}
                disabled={isPostFlow}
                onClick={onChange}
              />
            ) : null}

            <div className={cn(styles.line)} aria-hidden="true" />

            <Flag
              stage={right.stageNumber as Stage}
              value={String(idx + 2)}
              text={right.title}
              disabled={isPostFlow}
              onClick={(s) => {
                if (s < stage) onChange(s)
              }}
            />
          </li>
        )
      })}
    </ul>
  )
}

type FlagProps = {
  className?: string
  stage: Stage
  value: string
  text: string
  disabled: boolean
  onClick: (stage: Stage) => void
}

function Flag({ className, stage, value, text, disabled, onClick }: FlagProps) {
  const handleClick = useCallback(() => onClick(stage), [onClick, stage])

  return (
    <button
      type="button"
      className={cn(styles.flag, className)}
      onClick={handleClick}
      disabled={disabled}
    >
      <StageIcon />
      <span className={cn(styles.stage)}>{value}</span>
      <span className={cn(styles.text)}>{text}</span>
    </button>
  )
}
